import React, { useRef, useState } from 'react'
import { Alert, Button, Card, CardBody, Col, Label, Row } from 'reactstrap'
import QdContainer from 'components/Common/QdContainer'
import { v4 as uuidv4 } from 'uuid'
import { post } from 'utils/apiClient'
import AnalyseForm from './AnalyseForm'
import UploadForm from './UploadForm'
import DecisionsReview from './DecisionsReview'
import { fromApiQuestions, fromWritten, mergeSheetRows } from './rows'
import './OpenDockOnboard.scss'

const REVIEW_URL = 'location-management/opendock/onboard/review'
const APPLY_URL = 'location-management/opendock/onboard/apply'

const READ = 'read'
const REVIEW = 'review'

const ANALYSE_INPUT = 'analyse'
const UPLOAD_INPUT = 'upload'

// The page's own two screens. Unlike the status and action words, this navigation is not the
// endpoint's to state.
const STEPS = [[READ, 'Start'], [REVIEW, 'Review']]

// One line naming what was written, using the endpoint's own word for each action. Only the counts
// go on screen; which warehouse got what is in the downloadable file.
function formatCounts(countsByAction, vocabulary) {
    const actions = (vocabulary && vocabulary.actions) || {}
    const parts = Object.entries(countsByAction || {})
        .map(([action, count]) => `${count} ${(actions[action] || {}).label || action}`)
    return parts.length ? parts.join(', ') : 'nothing'
}

function Steps({ step }) {
    const reached = STEPS.findIndex(([name]) => name === step)
    return (
        <div className="d-flex gap-2 align-items-center m-2">
            {STEPS.map(([name, label], index) => (
                <React.Fragment key={name}>
                    {index > 0 && <span className="text-muted">›</span>}
                    <span className={index === reached ? 'fw-semibold' : 'text-muted'}>
                        {index < reached ? '✓ ' : ''}{label}
                    </span>
                </React.Fragment>
            ))}
        </div>
    )
}

function OpenDockOnboard() {
    const [step, setStep] = useState(READ)
    const [inputType, setInputType] = useState(ANALYSE_INPUT)
    // Held here so switching between the two ways in keeps what was typed. Both need the same
    // OpenDock login, and switching tabs unmounts whichever form was holding it.
    const [userId, setUserId] = useState('')
    const [password, setPassword] = useState('')
    const [reading, setReading] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [applying, setApplying] = useState(false)
    const [error, setError] = useState(null)
    // The run's name. Every batch of answers quotes it, so they all land in one report.
    const [jobId, setJobId] = useState(null)
    const [rows, setRows] = useState([])
    // The run's two sheets, to fill in elsewhere and bring back through Upload sheet: the questions
    // nobody could answer, and separately the values guessed and written anyway.
    const [questionsFile, setQuestionsFile] = useState(null)
    const [assumedFile, setAssumedFile] = useState(null)
    // The words for every status, action and count, as the endpoint states them. The screens hold
    // no vocabulary of their own, so a sheet off disk simply shows the plain word.
    const [vocabulary, setVocabulary] = useState(null)
    // The one thing a screen says about what was written: a line, and the file holding the rest.
    const [notice, setNotice] = useState(null)

    // The apply call signs in again, so the login is held for the length of the session and never
    // stored. A ref, so a re-render cannot leak it into anything that serialises state.
    const credentials = useRef(null)

    // The read already writes everything the bookings determine, so the review opens on what was
    // written as well as what could not be decided. A person arriving there has approved neither.
    const readBookings = async body => {
        setError(null)
        setReading(true)
        try {
            // The sheet goes to S3 and comes back as a link; the table is built from the rows.
            const response = await post(REVIEW_URL, body)
            credentials.current = { userId: body.userId, password: body.password }
            setJobId(response.jobId || null)
            setQuestionsFile(response.questionsFile || null)
            setAssumedFile(response.assumedFile || null)
            setVocabulary(response.vocabulary || null)
            setNotice({
                // What it configured, not the questions it could not: the questions have their
                // own link on the review screen below.
                text: `Seeded: ${formatCounts(response.seeded, response.vocabulary)}.`,
                file: response.configuredFile,
            })
            // One list: what it wrote without asking, then what it could not decide.
            setRows([...fromWritten(response.written), ...fromApiQuestions(response.questions)])
            setStep(REVIEW)
        } catch (err) {
            console.error(err)
            setError(err.message || 'Could not read the bookings.')
        } finally {
            setReading(false)
        }
    }

    // The other way in. A sheet can leave, be answered by someone else, and come back in a new
    // session, so this path reads nothing and writes nothing; it just opens the review on a file.
    // The login is still taken, because applying signs in again.
    const loadSheets = async (login, files) => {
        setError(null)
        setUploading(true)
        try {
            const sheets = await Promise.all(
                files.map(async file => ({ name: file.name, text: await file.text() })))
            const sheetRows = mergeSheetRows(sheets)
            credentials.current = login
            // A sheet off disk has no run behind it, and belongs to no job until Apply names one.
            setQuestionsFile(null)
            setAssumedFile(null)
            setVocabulary(null)
            setNotice(null)
            setJobId(null)
            setRows(sheetRows)
            setStep(REVIEW)
        } catch (err) {
            console.error(err)
            setError(err.message || 'Could not read those sheets.')
        } finally {
            setUploading(false)
        }
    }

    const updateRow = (id, changes) => setRows(
        current => current.map(row => (row.id === id ? { ...row, ...changes } : row)))

    // The answers go up as rows, in one call, quoting the run they answer. Applying again under the
    // same run adds to its report rather than starting another.
    const apply = async decisions => {
        setError(null)
        setApplying(true)
        try {
            const runId = jobId || uuidv4()
            const response = await post(APPLY_URL, {
                ...credentials.current,
                jobId: runId,
                decisions,
            })

            setJobId(runId)
            if (response.vocabulary) setVocabulary(response.vocabulary)
            setNotice({
                text: `Applied: ${formatCounts(response.seeded, response.vocabulary || vocabulary)}.`,
                file: response.appliedFile,
            })
        } catch (err) {
            console.error(err)
            setError(err.message || 'Could not apply the decisions.')
        } finally {
            setApplying(false)
        }
    }

    // Back to the first screen with the review intact. Nothing typed is thrown away: starting a new
    // run or loading another sheet replaces the rows, and that is a deliberate act with a visible
    // cause, where a Start over mid-list was neither.
    const goBack = () => {
        setError(null)
        setStep(READ)
    }

    const startOver = () => {
        credentials.current = null
        setUserId('')
        setPassword('')
        setJobId(null)
        setQuestionsFile(null)
        setAssumedFile(null)
        setVocabulary(null)
        setNotice(null)
        setRows([])
        setError(null)
        setStep(READ)
    }

    return (
        <QdContainer>
            <Card className="p-3">
                <Row>
                    <Col>
                        <span className="text-primary text-size-16 m-2">
                            Opendock Onboarding
                        </span>
                        <Steps step={step} />
                    </Col>
                </Row>
            </Card>

            {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

            {notice && (
                <Alert color="warning" toggle={() => setNotice(null)} className="py-2">
                    {notice.text}
                    {notice.file && notice.file.downloadUrl && (
                        <>
                            {' '}
                            <a
                                href={notice.file.downloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Download report
                            </a>.
                        </>
                    )}
                </Alert>
            )}

            {step === READ && rows.length > 0 && (
                <Alert color="light" className="d-flex justify-content-between align-items-center">
                    <span>{rows.length} settings in review, unsaved.</span>
                    <Button size="sm" color="primary" outline onClick={() => setStep(REVIEW)}>
                        Back to review
                    </Button>
                </Alert>
            )}

            {step === READ && (
                <Card className="p-2">
                    <CardBody>
                        <Row className="mb-4">
                            <Label className="col-sm-2 col-form-label">Input Type</Label>
                            <Col sm={7}>
                                <Row>
                                    <Col>
                                        <div className="form-check mb-3">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="odoInputType"
                                                id="odoInputAnalyse"
                                                value={ANALYSE_INPUT}
                                                checked={inputType === ANALYSE_INPUT}
                                                onChange={e => setInputType(e.target.value)}
                                            />
                                            <label className="form-check-label" htmlFor="odoInputAnalyse">
                                                Run analysis
                                            </label>
                                        </div>
                                    </Col>
                                    <Col>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="odoInputType"
                                                id="odoInputUpload"
                                                value={UPLOAD_INPUT}
                                                checked={inputType === UPLOAD_INPUT}
                                                onChange={e => setInputType(e.target.value)}
                                            />
                                            <label className="form-check-label" htmlFor="odoInputUpload">
                                                Upload sheet
                                            </label>
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        {inputType === ANALYSE_INPUT && (
                            <AnalyseForm
                                onRead={readBookings}
                                reading={reading}
                                userId={userId}
                                password={password}
                                setUserId={setUserId}
                                setPassword={setPassword}
                            />
                        )}
                        {inputType === UPLOAD_INPUT && (
                            <UploadForm
                                onUpload={loadSheets}
                                uploading={uploading}
                                userId={userId}
                                password={password}
                                setUserId={setUserId}
                                setPassword={setPassword}
                            />
                        )}
                    </CardBody>
                </Card>
            )}

            {step === REVIEW && (
                <DecisionsReview
                    rows={rows}
                    onChangeRow={updateRow}
                    onApply={apply}
                    applying={applying}
                    onBack={goBack}
                    onStartOver={startOver}
                    questionsFile={questionsFile}
                    assumedFile={assumedFile}
                    vocabulary={vocabulary}
                />
            )}
        </QdContainer>
    )
}

export default OpenDockOnboard
